import type { stacks } from "@dropbox/api-v2-client";
import { DEFAULT_SECTION_ID } from "@mirage/service-stacks/service/utils";
import { activeStackLinkSectionsMapAtom } from "@mirage/stacks/atoms";
import { useAtomValue } from "jotai";
import { useState } from "react";
import type { DraggableProvided } from "react-beautiful-dnd";
import { DroppableEmptySection } from "../../DragAndDrop/DroppableEmptySection";
import { Section } from "./Section";

interface SectionAreaProps {
  section: stacks.Section;
  draggableProps?: DraggableProvided;
}

export const SectionArea: React.FC<SectionAreaProps> = ({
  section,
  draggableProps,
}) => {
  const linkSectionMap = useAtomValue(activeStackLinkSectionsMapAtom);
  /**
   * Keep track of the DroppableEmptySection that the user is currently hovering over.
   * The DND library seems to not always fire when a user has stopped hovering over
   * a DroppableEmptySection, so we are keeping track of this state in the parent
   * to ensure that only one DroppableEmptySection is highlighted at a time.
   */
  const [emptySectionIdHoveringOver, setEmptySectionIdHoveringOver] = useState<
    string | null
  >(null);

  const sectionId = section.id ?? DEFAULT_SECTION_ID;
  const items = linkSectionMap ? (linkSectionMap.get(sectionId) ?? []) : [];

  return (
    <div ref={draggableProps?.innerRef} {...draggableProps?.draggableProps}>
      <Section
        section={section}
        items={items}
        dragHandleProps={draggableProps?.dragHandleProps || undefined}
      />
      <DroppableEmptySection
        sectionId={sectionId}
        isHoveringForMoment={emptySectionIdHoveringOver === sectionId}
        onDragEnterMoment={(hovering: boolean) => {
          setEmptySectionIdHoveringOver(hovering ? sectionId : null);
        }}
        mustCreateNewSection={
          sectionId !== DEFAULT_SECTION_ID || items.length !== 0
        }
        hideDivider
      />
    </div>
  );
};
